import {
	Profiler,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { VelocityWindow } from '../window/velocityWindow';
import { notify } from '../utils/notifications';
import { useTrackFPS } from 'src/hooks/dev/useTrackFPS';
import { PERFORMANCE_TESTING_LABELS } from '@velocity-exchange/common';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

interface ProfileStats {
	totalRenders: number;
	totalActualDuration: number;
	totalBaseDuration: number;
	durations: number[];
}

const EMPTY_STATS: ProfileStats = {
	totalRenders: 0,
	totalActualDuration: 0,
	totalBaseDuration: 0,
	durations: [],
};

/**
 * Component which adds a profiler to the app. It will start profiling when triggered by the dev switch. This SHOULD NOT AFFECT PERFORMANCE IN PRODUCTION because it is disabled by default in production unless you use a special build - see https://react.dev/reference/react/Profiler
 *
 * You can confirm by testing the profiler on a production build and you'll see that no stats get recorded.
 * @param param0
 * @returns
 */
const ProfilerWrapper = ({ children }: { children: React.ReactNode }) => {
	const alreadyProfiling = useRef(false);
	const profilerEnabledRef = useRef(false);
	const profileStats = useRef<ProfileStats>({ ...EMPTY_STATS });
	const profilingStartTime = useRef<number>(0);
	const profilingEndTime = useRef<number>(0);
	const [fpsEnabled, setFpsEnabled] = useState(false);
	const fpsSamples = useTrackFPS(fpsEnabled);
	const logProfileStatsRef = useRef<() => void>(() => {});

	const avgFps = useMemo(() => {
		if (fpsSamples.length === 0) return 0;
		const avgFrameTime =
			fpsSamples.reduce((a, b) => a + b, 0) / fpsSamples.length;
		if (avgFrameTime === 0) return 0;
		return 1000 / avgFrameTime;
	}, [fpsSamples]);

	const avgFpsRef = useSyncedRef(avgFps);

	const calculateStats = useCallback((durations: number[]) => {
		if (durations.length === 0)
			return { min: 0, median: 0, max: 0, p90: 0, mean: 0 };

		const sorted = [...durations].sort((a, b) => a - b);
		const min = sorted[0];
		const max = sorted[sorted.length - 1];
		const median =
			sorted.length % 2 === 0
				? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
				: sorted[Math.floor(sorted.length / 2)];

		const p90Index = Math.floor(sorted.length * 0.9);
		const p90 = sorted[p90Index];

		const mean =
			durations.reduce((sum, duration) => sum + duration, 0) / durations.length;

		const frequency: { [key: number]: number } = {};
		durations.forEach((d) => (frequency[d] = (frequency[d] || 0) + 1));

		return { min, median, max, p90, mean };
	}, []);

	useEffect(() => {
		logProfileStatsRef.current = () => {
			const stats = profileStats.current;
			const totalDurationMs =
				profilingEndTime.current - profilingStartTime.current;
			const totalDurationSec = totalDurationMs / 1000;

			const avgRendersPerSec = stats.totalRenders / totalDurationSec;
			const avgDurationPerSec = stats.totalActualDuration / totalDurationSec;
			const calculatedStats = calculateStats(stats.durations);

			console.group('🔍 Profile Statistics');
			console.log(
				`See https://react.dev/reference/react/Profiler#onrender-callback for more information about some these statistics`
			);
			console.log(
				`📊 Profiling Results:\n\n` +
					`${
						PERFORMANCE_TESTING_LABELS.totalDurationMs
					}: ${totalDurationMs.toFixed(2)}ms (${totalDurationSec.toFixed(
						2
					)}s)\n` +
					`${PERFORMANCE_TESTING_LABELS.totalRenders}: ${stats.totalRenders}\n` +
					`${
						PERFORMANCE_TESTING_LABELS.avgRendersPerSec
					}: ${avgRendersPerSec.toFixed(2)}\n` +
					`${
						PERFORMANCE_TESTING_LABELS.avgDurationPerSec
					}: ${avgDurationPerSec.toFixed(2)}ms, (${(
						(avgDurationPerSec / 1000) *
						100
					).toFixed(2)}%)\n\n` +
					`${
						PERFORMANCE_TESTING_LABELS.totalActualDuration
					}: ${stats.totalActualDuration.toFixed(2)}ms\n` +
					`${PERFORMANCE_TESTING_LABELS.avgDurationPerSec}: ${(
						stats.totalActualDuration / stats.totalRenders
					).toFixed(2)}ms\n` +
					`${PERFORMANCE_TESTING_LABELS.min}: ${calculatedStats.min.toFixed(
						2
					)}ms\n` +
					`${PERFORMANCE_TESTING_LABELS.mean}: ${calculatedStats.mean.toFixed(
						2
					)}ms\n` +
					`${
						PERFORMANCE_TESTING_LABELS.median
					}: ${calculatedStats.median.toFixed(2)}ms\n` +
					`${PERFORMANCE_TESTING_LABELS.max}: ${calculatedStats.max.toFixed(
						2
					)}ms\n` +
					`${PERFORMANCE_TESTING_LABELS.p90}: ${calculatedStats.p90.toFixed(
						2
					)}ms\n\n` +
					`${PERFORMANCE_TESTING_LABELS.avgFPS}: ${avgFpsRef.current.toFixed(
						2
					)}\n` +
					`${PERFORMANCE_TESTING_LABELS.avgEventLoopLag}: n/a`
			);
			console.groupEnd();
		};
	}, [calculateStats]);

	useEffect(() => {
		const handleProfileFor5Secs = () => {
			if (alreadyProfiling.current) {
				notify({ type: 'warning', message: 'already profiling' });
				return;
			}

			notify({ type: 'info', message: 'profiling for 5 seconds' });
			setFpsEnabled(true);
			alreadyProfiling.current = true;
			profileStats.current = { ...EMPTY_STATS };
			profilingStartTime.current = performance.now();
			profilerEnabledRef.current = true;
			setTimeout(() => {
				setFpsEnabled(false);
				profilerEnabledRef.current = false;
				profilingEndTime.current = performance.now();
				alreadyProfiling.current = false;
				logProfileStatsRef.current();
				notify({ type: 'info', message: 'profiling finished, see console' });
			}, 5000);
		};

		const handleStartProfiling = () => {
			if (alreadyProfiling.current) {
				notify({ type: 'warning', message: 'already profiling' });
				return;
			}

			notify({ type: 'info', message: 'starting profiling' });
			setFpsEnabled(true);
			alreadyProfiling.current = true;
			profileStats.current = { ...EMPTY_STATS };
			profilingStartTime.current = performance.now();
			profilerEnabledRef.current = true;
		};

		const handleStopProfiling = () => {
			if (!alreadyProfiling.current) {
				notify({ type: 'warning', message: 'not profiling' });
				return;
			}

			profilerEnabledRef.current = false;
			profilingEndTime.current = performance.now();
			alreadyProfiling.current = false;
			setFpsEnabled(false);
			logProfileStatsRef.current();

			notify({ type: 'info', message: 'profiling finished, see console' });
		};

		VelocityWindow.devEventEmitter.on(
			'profile_for_5_secs',
			handleProfileFor5Secs
		);
		VelocityWindow.devEventEmitter.on('start_profiling', handleStartProfiling);
		VelocityWindow.devEventEmitter.on('stop_profiling', handleStopProfiling);

		return () => {
			VelocityWindow.devEventEmitter.off(
				'profile_for_5_secs',
				handleProfileFor5Secs
			);
			VelocityWindow.devEventEmitter.off(
				'start_profiling',
				handleStartProfiling
			);
			VelocityWindow.devEventEmitter.off('stop_profiling', handleStopProfiling);
		};
	}, []);

	const handleProfileRender = useCallback(
		(
			_id: string,
			_phase: string,
			actualDuration: number,
			baseDuration: number,
			_startTime: number,
			_commitTime: number
		) => {
			if (!profilerEnabledRef.current) return;

			const stats = profileStats.current;
			stats.totalRenders++;
			stats.totalActualDuration += actualDuration;
			stats.totalBaseDuration += baseDuration;
			stats.durations.push(actualDuration);
		},
		[]
	);

	return (
		<Profiler id="root-layout" onRender={handleProfileRender}>
			{children}
		</Profiler>
	);
};

export default ProfilerWrapper;
